'use client';

import { Link } from '@/i18n/navigation';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import useFn from '@/hooks/use-fn';
import NiceModal from '@ebay/nice-modal-react';
import { GameStatus } from '@/features/casino/services/home';
import { selectedCurrencyAtom } from '@/store/balance';
import { userAtom } from '@/store';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import ImageLoad from './image-load';
import Maintenance from './maintenance';
import ModalWarn from './modal-warn';
import GameCardWithTracking, { GameTrackingData } from './game-card-with-tracking';

// 通用的游戏项接口，支持不同的数据结构
export interface GameCardItem {
  id: number;
  status: number;
  currency_type?: string[];
  // 图片属性：CardNormalItem 使用 icon，GameItem 使用 game_img
  icon?: string;
  game_img?: string;
  // 曝光埋点需要的属性
  vendor_id?: string;
  game_code?: string;
  game_type?: string | number;
  game_category_id?: string;
  name?: string;
  // 其他可能的属性
  [key: string]: any;
}

interface GameCardWithExposureProps {
  item: GameCardItem;
  index: number;
  currentPage?: string;
  currentModule?: string;
  onNavigate?: (e: any) => Promise<void>;
  priority?: boolean;
  className?: string;
  skeletonWidth?: number;
  skeletonHeight?: number;
}

export default function GameCardWithExposure({
  item,
  index,
  currentPage = 'Home',
  currentModule = 'casino',
  onNavigate,
  priority = false,
  className = '',
  skeletonWidth,
  skeletonHeight,
}: GameCardWithExposureProps) {
  const t = useTranslations('home');
  const user = useAtomValue(userAtom);
  const userGlobalBalanceCode = useAtomValue(selectedCurrencyAtom);
  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });

  // 生成链接
  const getLink = () => {
    if (user.accessToken && item.currency_type?.includes(userGlobalBalanceCode || '')) {
      return `/casino/games/play?id=${item.id}_${item.name}&currency=${userGlobalBalanceCode}`;
    } else {
      return `/casino/games/detail?id=${item.id}_${item.name}`;
    }
  };

  // 处理不可用状态
  const handleUnavailable = useFn((imgUrl: string) => {
    NiceModal.show(ModalWarn, {
      title: t('gameError'),
      imgUrl: imgUrl,
      imgWidth: 130,
      imgHeight: 171,
    });
  });

  // 获取图片 URL
  const getImageUrl = () => {
    return item.game_img || item.icon || '';
  };

  // 将 GameCardItem 转换为 GameTrackingData
  const trackingData: GameTrackingData = {
    game_id: item.id || item?.game_id,
    game_name: item.name,
    vendor_id: item.vendor_id,
    provider_id: item.provider_id,
    game_code: item.game_code,
    game_type: item.game_type,
    game_category_id: item.game_category_id,
  };

  // 默认的导航处理（现在只处理导航，状态检查和埋点已在 wrappedNavigate 中处理）
  const defaultOnNavigate = async (_e: any) => {
    // 导航逻辑已移至 wrappedNavigate
  };

  const handleNavigate = onNavigate || defaultOnNavigate;
  const imageUrl = getImageUrl();
  const bgGradient = 'linear-gradient(150.15deg, #2283f625 27.17%, #2283f615 46.76%, #2283f600 61.36%), #2a3546';
  const link = getLink();
  return (
    <GameCardWithTracking
      item={trackingData}
      index={index}
      currentPage={currentPage}
      currentModule={currentModule}
      throttleWait={30 * 1000}
    >
      {({ cardRef, handleClick }) => {
        // 包装 handleNavigate 以包含点击埋点
        const wrappedNavigate = async (e: any) => {
          playAudio();
          if (item.status !== GameStatus.NORMAL) {
            handleUnavailable(getImageUrl());
            e.preventDefault();
            return;
          }
          // 只有在游戏状态正常时才发送点击埋点
          await handleClick();
          await handleNavigate(e);
        };

        return (
          <div
            ref={cardRef}
            className={`aspect-[130/171] active:scale-90 relative rounded-[10px] overflow-hidden cursor-pointer group transition-all duration-300 ease-out hover:shadow-2xl transform-gpu will-change-transform ${className}`}
          >
            <Link href={link} onNavigate={wrappedNavigate}>
              {imageUrl ? (
                <ImageLoad
                  src={imageUrl}
                  className={'w-full h-full object-cover'}
                  alt={item?.name || 'card'}
                  fill
                  priority={priority || index < 3}
                  skeletonWidth={skeletonWidth}
                  skeletonHeight={skeletonHeight}
                />
              ) : (
                <div className='w-full h-full' style={{ background: bgGradient }} />
              )}
            </Link>
            {item.status !== GameStatus.NORMAL && <Maintenance />}
          </div>
        );
      }}
    </GameCardWithTracking>
  );
}

